import { useState, useEffect, useRef, useContext } from 'react';
import { EffectFade } from 'swiper/modules';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';

// Import Swiper styles
import 'swiper/css';

import cssStyles from './article.module.css';

import { Article } from './Article';
import { VirtualArticles } from './virtual/VirtualArticles';

import { useDeviceInformation } from '../../hooks/use-element';
import { DeviceType } from './CSS';
import { ArticlesContext, ScrollDirection } from './Article.context';

export function Articles(props: Readonly<ArticlesProps>) {
	const {
		index,
		swiper,
		setSwiper,
		onTransitionEnd,
		articles,
		lang,
		fontSize,
		styles,
		isTouchDevice,
		setScrollDirection,
		virtual,
	} = props;
	const [components, setComponents] = useState<Array<any>>([]);

	useEffect(() => {
		setComponents(articles.map(mapComponent()));
	}, []);

	useEffect(() => {
		if (!swiper) {
			return;
		}
		swiper.slideTo(index, 750, false);
	}, [index, swiper]);

	useEffect(() => {
		if (!styles) {
			return;
		}

		if (!styles.size) {
			return;
		}
	}, [styles]);

	return (
		<ArticlesContext.Provider
			value={{
				selectedIndex: index,
				lang,
				setScrollDirection,
				isTouchDevice: !!isTouchDevice,
				styles,
			}}
		>
			<div
				className={cssStyles['articles-container']}
				style={{ fontSize: fontSize ? `${fontSize}%` : undefined }}
			>
				{virtual ? (
					<VirtualArticles {...props} />
				) : (
					<Swiper
						resistance={false}
						modules={[EffectFade]}
						updateOnWindowResize={true}
						initialSlide={index}
						virtual
						scrollbar={{
							draggable: false,
						}}
						allowTouchMove={isTouchDevice}
						onSwiper={(s: SwiperClass) => {
							setSwiper(s);
						}}
						onSlideChange={onTransitionEnd}
					>
						{components}
					</Swiper>
				)}
			</div>
		</ArticlesContext.Provider>
	);
}

interface ArticlesProps {
	lang: string;
	fontSize: number;
	virtual?: boolean;
	styles: Map<string, any>;
	articles: Array<any>;
	isTouchDevice: boolean | undefined;
	index: number;
	onTransitionEnd: (s: SwiperClass) => void;
	swiper: SwiperClass | null;
	setSwiper: (s: SwiperClass) => void;
	setScrollDirection?: (direction: ScrollDirection) => void;
	device: DeviceType;
	setNavbarVisibility: any;
	navigateToArticle?: (articleId: number) => void;
}

function mapComponent() {
	// eslint-disable-next-line react/display-name
	return (article: any, index: number) => {
		const { id } = article;

		return (
			<SwiperSlide
				virtualIndex={index}
				key={id}
				className={cssStyles['article-swiper']}
			>
				<SwiperArticle article={article} index={index} />
			</SwiperSlide>
		);
	};
}

function SwiperArticle({ article, index }: Readonly<SwiperArticleProps>) {
	const { id, style, components, ArticleID } = article;
	const { selectedIndex, setScrollDirection, isTouchDevice, lang, styles } =
		useContext(ArticlesContext);
	const ref = useRef<any>();
	const device = useDeviceInformation();
	useScroll({
		ref,
		threshold: 100,
		isSelected: selectedIndex === null ? false : selectedIndex === index,
		cb: (direction: ScrollDirection) => {
			if (!setScrollDirection) {
				return;
			}
			setScrollDirection(direction);
		},
	});

	return (
		<div ref={ref}>
			<Article
				id={id}
				ArticleId={ArticleID}
				index={index}
				style={`${style}`}
				lang={lang}
				device={device ? device.type : 'mobile'}
				isVoid={false}
				styles={styles}
				isTouchDevice={isTouchDevice}
				components={components}
			/>
		</div>
	);
}

interface SwiperArticleProps {
	article: any;
	index: number;
}

/**
 * React hook that says in which direction the scroll is moving
 *
 */
function useScroll(args: UseScrollArgs) {
	const { ref, threshold, isSelected, cb } = args;
	useEffect(() => {
		if (!ref || !ref.current) {
			return;
		}

		let lastScrollY = ref.current.scrollTop;

		const updateScrollDir = (scrollY: number) => {
			if (Math.abs(scrollY - lastScrollY) < threshold) {
				return;
			}

			cb(scrollY > lastScrollY ? 'down' : 'up');
			lastScrollY = scrollY > 0 ? scrollY : 0;
		};

		const onScroll = (e: any) => {
			const { scrollTop } = e.target;
			updateScrollDir(scrollTop);
		};

		if (isSelected) {
			ref.current.addEventListener('scroll', onScroll);
		} else {
			ref.current.removeEventListener('scroll', onScroll);
		}
	}, [ref, isSelected]);
}

interface UseScrollArgs {
	ref: any;
	threshold: number;
	isSelected: boolean;
	cb: (direction: ScrollDirection) => void;
}
